<template>
  <aside class="sidebar">
    
    <!-- ========== LOGO SUPERIOR Y NOMBRE DE MARCA ========== -->
    <div class="sidebar__top">
      <img src="@/assets/logoKiitosSinFondo.png" alt="Logo Kiitos" class="sidebar__logo" />
      
      <span class="sidebar__brand"></span>
    </div>

    <!-- ========== MENÚ DE NAVEGACIÓN ========== -->
    <nav class="sidebar__menu">
      <ul>
        <li
          v-for="item in navItems"
          :key="item.label"
          :class="['sidebar__item', { active: isActive(item.route) }]" 
          @click="handleNavigation(item)"  
        >
          <ion-icon :name="item.icon" class="sidebar__icon"></ion-icon>
          <span>{{ item.label }}</span>
        </li>
      </ul>
    </nav>

    <!-- ========== CERRAR SESIÓN ========== -->
    <div class="sidebar__bottom">
      <button class="sidebar__logout" @click="signOut">
        <ion-icon name="log-out-outline"></ion-icon>
        <span>Cerrar sesión</span>
      </button>
    </div>

  </aside>
</template>

<script>
export default {
  name: "SideBar",

  // ===============================
  // DATOS REACTIVOS
  // ===============================
  data() {
    return {
      /**
       * Lista completa de ítems del menú lateral
       * Cada uno tiene:
       *  - label: Texto visible
       *  - icon: Nombre del ícono (Ionicons)
       *  - route: Ruta a navegar
       */
      rawNavItems: [
        { label: 'Métricas', icon: 'grid-outline', route: '/metrics' },
        { label: 'Chat', icon: 'chatbubble-outline', route: '/chat' },
        // { label: 'Chatbot', icon: 'settings-outline', route: '/chatbot' },
        { label: 'Clientes', icon: 'book-outline', route: '/contacts' },
        { label: 'Usuarios', icon: 'person-outline', route: '/users' },
        { label: 'Líneas', icon: 'call-outline', route: '/lines' },
      ],

      /**
       * Rol actual del usuario
       * Se obtiene desde localStorage
       */
      userRole: localStorage.getItem("user_role") || "agent"
    };
  },

  // ===============================
  // PROPIEDADES COMPUTADAS
  // ===============================
  computed: {
    /**
     * Ítems visibles según el rol:
     * - Si es admin → muestra todo
     * - Si es agent → oculta 'Usuarios' y 'Líneas'
     */
    navItems() {
      if (this.userRole === 'admin') {
        return this.rawNavItems;
      }

      // Si es agent, filtra los ítems prohibidos
      return this.rawNavItems.filter(item =>
        item.label !== 'Usuarios' && item.label !== 'Líneas'
      );
    }
  },

  // ===============================
  // MÉTODOS
  // ===============================
  methods: {
    /**
     * Devuelve true si la ruta pasada coincide con la ruta actual
     * Se usa para aplicar clase "activa"
     */
    isActive(route) {
      return this.$route.path === route;
    },

    /**
     * Redirige al hacer clic en un ítem, si no estás ya en la ruta
     */
    handleNavigation(item) {
      if (this.$route.path !== item.route) {
        this.$router.push(item.route);
      }
    },

    /**
     * Cierra sesión limpiando el localStorage
     * y redirige al login
     */
    signOut() {
      localStorage.removeItem('token');
      localStorage.removeItem('user_role');
      localStorage.removeItem('user_id');
      window.location.href = '/';
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@600;700&display=swap');

/* ========== CONTENEDOR PRINCIPAL DEL SIDEBAR ========== */
.sidebar {
  height: 87%; 
  width: 220px;
  background-color: #f1f5f9; 
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between; 
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.03); 
}

/* ========== SECCIÓN SUPERIOR (Logo + Marca) ========== */
.sidebar__top {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
  position: relative;
}

.sidebar__top::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0.5rem;
  right: 0.5rem;
  height: 1.5px;
  background-color: #0f172a;
  border-radius: 999px;
}

.sidebar__logo {
  width: 39px;
  height: 39px;
  object-fit: contain;
  filter: grayscale(100%) brightness(0); 
}

.sidebar__brand::before {
  content: 'Kiitos';
  font-family: 'Manrope', sans-serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: #1e293b;
  letter-spacing: -0.4px;
}

/* ========== MENÚ DE NAVEGACIÓN ========== */
.sidebar__menu ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.sidebar__item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.65rem;
  cursor: pointer;
  transition: all 0.25s ease;
  color: #1e293b;
  font-weight: 400;
  font-size: 0.95rem;
  font-family: 'Manrope', sans-serif;
  position: relative;
}

.sidebar__item:hover {
  background-color: #CCCCFF;
}

.sidebar__item.active {
  background-color: #3b82f6; 
  color: #ffffff;
}
.sidebar__item.active .sidebar__icon {
  color: #ffffff;
}

.sidebar__icon {
  font-size: 1.2rem;
  color: #64748b;
  transition: color 0.3s;
}

/* ========== SECCIÓN INFERIOR: CERRAR SESIÓN ========== */
.sidebar__bottom {
  margin-top: 1.5rem;
  position: relative;
  padding-top: 1.25rem;
}

.sidebar__bottom::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0.5rem;
  right: 0.5rem;
  height: 1px;
  background-color: #0f172a;
  border-radius: 999px;
}

.sidebar__logout {
  all: unset;
  width: 88%;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.7rem 1rem;
  border-radius: 0.65rem;
  font-family: 'Manrope', sans-serif;
  font-size: 0.925rem;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  transition: background 0.3s ease, color 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  line-height: 1;
}

.sidebar__logout ion-icon {
  font-size: 1.05rem;
  color: #94a3b8;
  transition: color 0.3s ease;
}

.sidebar__logout:hover {
  background: rgba(100, 116, 139, 0.15);
  color: #1e293b;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.sidebar__logout:hover ion-icon {
  color: #1e293b;
}

.sidebar__logout:active {
  background-color: rgba(226, 232, 240, 0.5);
  box-shadow: inset 0 0 0 1px rgba(100, 116, 139, 0.2);
}
</style>